import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { eq } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import { capitalize } from 'frontend-utils/utils/convert-case';
import SettingsBudgetForecastVersionHistoryDrawerItem from 'lumen/components/settings/budget-forecast/version-history-drawer/item';
import type { BudgetForecastVersion } from 'lumen/models';

import styles from './group.module.css';

interface SettingsBudgetForecastVersionHistoryDrawerGroupSignature {
  Args: {
    date: Dayjs;
    onSelectVersion: (versionId: string) => void;
    selectedVersionId: string | undefined;
    versions: BudgetForecastVersion[];
  };
}

export default class SettingsBudgetForecastVersionHistoryDrawerGroup extends Component<SettingsBudgetForecastVersionHistoryDrawerGroupSignature> {
  @service declare intl: Services['intl'];

  @tracked isOpen = this.isGroupActive;

  get groupLabel(): string {
    const today = dayjs();

    const diffInDays = today.diff(this.args.date, 'day');
    if (diffInDays > 1) {
      return this.args.date.format('D MMM YYYY');
    }

    // Use relative time for "Today" and "Yesterday"
    const relativeLabel = this.intl.formatRelativeTime(-diffInDays, {
      unit: 'day',
      numeric: 'auto',
    });
    return capitalize(relativeLabel);
  }

  get isGroupActive(): boolean {
    return this.args.versions.some((v) => v.id === this.args.selectedVersionId);
  }

  @action
  toggleOpen(event: Event): void {
    this.isOpen = (event.currentTarget as HTMLDetailsElement).open;
  }

  <template>
    <details
      class={{styles.container}}
      data-test-group
      open={{this.isOpen}}
      {{on "toggle" this.toggleOpen}}
    >
      <summary class={{styles.header}}>
        <span
          class={{local styles "bullet" (if this.isGroupActive "is-active")}}
          data-test-group-active={{this.isGroupActive}}
          data-test-group-bullet
        ></span>

        <span class="text-300--medium" data-test-group-label>
          {{this.groupLabel}}
        </span>

        {{#if this.isOpen}}
          <PrismIcon
            @name="chevron-up"
            class="ml-auto"
            data-test-icon="chevron-up"
          />
        {{else}}
          <PrismIcon
            @name="chevron-down"
            class="ml-auto"
            data-test-icon="chevron-down"
          />
        {{/if}}
      </summary>

      <div class={{styles.items-container}}>
        {{#each @versions as |version|}}
          <SettingsBudgetForecastVersionHistoryDrawerItem
            @isActive={{eq version.id @selectedVersionId}}
            @onSelectVersion={{fn @onSelectVersion version.id}}
            @version={{version}}
          />
        {{/each}}
      </div>
    </details>
  </template>
}
